import React, { useMemo, useState } from 'react';
import { JOURNEY, dueLabel, nextStep, isOpen } from './deskModel';
import { OwnerAvatar } from './TodayView';

const PER_COLUMN = 30;

// Dropping a card on a column moves it to that column's first stage; steps that need details open the lead.
const DROP_STAGE = { new: 'identified', contacted: 'contacted', interested: 'replied', swatch: 'swatch_requested', delivered: 'swatch_delivered', sample: 'sampling', quote: 'quote_sent', won: 'bulk_client' };

export default function BoardView({ leads, today, team, onOpen, onMove }) {
  const [over, setOver] = useState('');
  const [onlyLate, setOnlyLate] = useState(false);
  const columns = useMemo(() => JOURNEY.map((j) => {
    const all = leads.filter((l) => isOpen(l) && !l.keptOut && j.stages.includes(l.stage));
    const shown = (onlyLate ? all.filter((l) => l.followUpDate && l.followUpDate < today) : all)
      .sort((a, b) => String(a.followUpDate || '9999').localeCompare(String(b.followUpDate || '9999')));
    return { ...j, count: all.length, cards: shown.slice(0, PER_COLUMN), more: Math.max(0, shown.length - PER_COLUMN) };
  }), [leads, onlyLate, today]);

  return (
    <div>
      <section className="bd-funnel" aria-label="Funnel">
        <span className="bd-k" style={{ marginRight: 16 }}>Open leads by stage</span>
        {columns.map((c, i) => {
          const next = columns[i + 1];
          return (
            <div key={c.key} style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ display: 'flex', flexDirection: 'column', padding: '0 12px' }}>
                <span className="bd-mono" style={{ fontSize: 18, fontWeight: 600 }}>{c.count}</span>
                <span style={{ fontSize: 12, color: 'var(--bd-muted)' }}>{c.label}</span>
              </div>
              {next && <span aria-hidden="true" style={{ color: 'var(--bd-line-strong)' }}>›</span>}
            </div>
          );
        })}
        <span style={{ flex: 1 }} />
        <button type="button" className={`bd-btn bd-btn--sm ${onlyLate ? 'bd-btn--mari' : 'bd-btn--ghost'}`} aria-pressed={onlyLate} onClick={() => setOnlyLate((v) => !v)}>Only overdue</button>
      </section>
      <div className="bd-board-wrap">
        <div className="bd-board">
          {columns.map((c) => (
            <section
              key={c.key}
              className={`bd-col ${over === c.key ? 'over' : ''}`}
              aria-label={`${c.label}, ${c.count} leads`}
              onDragOver={(e) => { e.preventDefault(); setOver(c.key); }}
              onDragLeave={() => setOver('')}
              onDrop={(e) => { e.preventDefault(); setOver(''); const id = e.dataTransfer.getData('text/plain'); const lead = leads.find((l) => l.id === id); if (lead && !c.stages.includes(lead.stage)) onMove(lead, DROP_STAGE[c.key]); }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '2px 4px' }}>
                <h2 style={{ fontSize: 14, fontWeight: 700 }}>{c.label} <span className="bd-mono" style={{ fontWeight: 500, color: 'var(--bd-muted)' }}>{c.count}</span></h2>
              </div>
              {c.cards.map((lead) => {
                const due = dueLabel(lead, today);
                return (
                  <button
                    key={lead.id}
                    type="button"
                    className="bd-card"
                    draggable
                    onDragStart={(e) => e.dataTransfer.setData('text/plain', lead.id)}
                    onClick={() => onOpen(lead)}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start', width: '100%' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                        <strong style={{ fontSize: 14 }}>{lead.brandName}</strong>
                        <span style={{ fontSize: 12, color: 'var(--bd-muted)' }}>{[lead.city, lead.country].filter(Boolean).join(', ')}</span>
                      </div>
                      <OwnerAvatar email={lead.owner} team={team} />
                    </div>
                    <span style={{ fontSize: 13, lineHeight: 1.4, color: 'var(--bd-ink-2)' }}>{nextStep(lead, today).text}</span>
                    <span className={`bd-mono bd-due--${due.tone}`} style={{ fontSize: 11, alignSelf: 'flex-end' }}>{due.text}</span>
                  </button>
                );
              })}
              {c.more > 0 && <span style={{ fontSize: 12, color: 'var(--bd-muted)', padding: '4px' }}>+{c.more} more. Use search or Accounts to find them.</span>}
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
